import { useEffect, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { database, ref, onValue, off } from "../../config/firebase";
import {
  setIcpProgress,
  setCampaignProgress,
  clearProgress,
} from "../redux/actions/firebaseProgressActions";

const TERMINAL_STATES = [
  "scheduled", "active", "paused", "completed", "interrupted", "inactive",
];

/**
 * Centralized Firebase progress sync - lives at DashboardLayout level.
 * Listeners persist across screen navigations. Only subscribes/unsubscribes
 * when the campaign list changes (new campaigns added or campaigns reach terminal state).
 */
const useFirebaseProgressSync = () => {
  const dispatch = useDispatch();
  const listenersRef = useRef({});

  // Read campaign list from Redux
  const records =
    useSelector((state) => state.campaignsListFetch?.payload?.data?.records) || [];

  // Stable subscribe/unsubscribe functions via dispatch ref
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;

  const subscribe = useCallback((campaignId) => {
    if (listenersRef.current[campaignId]) return; // already listening

    const icpRef = ref(database, `icp_progress/${campaignId}`);
    const campRef = ref(database, `campaign_progress/${campaignId}`);

    onValue(icpRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        dispatchRef.current(setIcpProgress(campaignId, {
          completed: data.completed || 0,
          total: data.total || 0,
          percentage: data.percentage || 0,
          state: data.state || null,
        }));
      } else {
        dispatchRef.current(setIcpProgress(campaignId, null));
      }
    });

    onValue(campRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        dispatchRef.current(setCampaignProgress(campaignId, {
          completed: data.completed || 0,
          total: data.total || 0,
          percentage: data.percentage || 0,
          state: data.state || null,
        }));
      } else {
        dispatchRef.current(setCampaignProgress(campaignId, null));
      }
    });

    listenersRef.current[campaignId] = { icpRef, campRef };
  }, []);

  const unsubscribe = useCallback((campaignId) => {
    const entry = listenersRef.current[campaignId];
    if (!entry) return;

    if (entry.icpRef) off(entry.icpRef);
    if (entry.campRef) off(entry.campRef);
    delete listenersRef.current[campaignId];
    dispatchRef.current(clearProgress(campaignId));
  }, []);

  // Sync listeners with campaign list - runs whenever records change
  useEffect(() => {
    // Build set of campaign IDs that need listeners
    const neededIds = new Set();
    records.forEach((r) => {
      if (r.type !== "file") return;
      const s = (r.state || "").toLowerCase();
      if (!TERMINAL_STATES.includes(s)) {
        neededIds.add(r._id);
      }
    });

    // Subscribe to new campaigns
    neededIds.forEach((id) => subscribe(id));

    // Unsubscribe campaigns no longer in the processing list
    Object.keys(listenersRef.current).forEach((id) => {
      if (!neededIds.has(id)) {
        unsubscribe(id);
      }
    });

  }, [records, subscribe, unsubscribe]);

  // Cleanup ONLY on true unmount (DashboardLayout unmount = logout)
  useEffect(() => {
    return () => {
      Object.keys(listenersRef.current).forEach((id) => {
        const entry = listenersRef.current[id];
        if (entry.icpRef) off(entry.icpRef);
        if (entry.campRef) off(entry.campRef);
      });
      listenersRef.current = {};
    };
  }, []);
};

export default useFirebaseProgressSync;
